/**
 * PilotagePage.js — Tableaux Dirigeant (Lot 2)
 * =============================================
 * Entrées / sorties / stock / CA construits sur les APIs /api/pilotage/*
 * (journal des mouvements + service financier central du Lot 1).
 * Export Excel multi-feuilles pour préparer les réunions commerciales.
 */
import React, { useCallback, useEffect, useState } from 'react';
import api from '../../lib/api';
import { toast } from 'sonner';
import {
    BarChart3, Download, ArrowDownToLine, ArrowUpFromLine, Package,
    TrendingUp, Euro, Loader2, ChevronLeft, ChevronRight, CheckCircle2,
    AlertTriangle, ClipboardList, FileUp, ArrowUpRight, ArrowDownRight,
} from 'lucide-react';
import MeetingAgendaDialog from '../../components/pilotage/MeetingAgendaDialog';
import ImportHistoryDialog from '../../components/pilotage/ImportHistoryDialog';
import PilotageAlertsBanner from '../../components/pilotage/PilotageAlertsBanner';
import PilotageActionsPanel from '../../components/pilotage/PilotageActionsPanel';
import TargetsHistoryChart from '../../components/pilotage/TargetsHistoryChart';

const PERIODS = [
    { key: 'month', label: 'Mois' },
    { key: 'quarter', label: 'Trimestre' },
    { key: 'year', label: 'Année' },
];

const EVENT_LABELS = {
    created: 'Création', activated: 'Activation', sold: 'Vente',
    denounced: 'Dénonciation', terminated: 'Résiliation', expired: 'Expiration',
    withdrawn: 'Retrait', renewed: 'Renouvellement', reassigned: 'Réassignation',
    offer_received: 'Offre reçue', offer_accepted: 'Offre acceptée',
    compromis_started: 'Compromis en cours', compromis_signed: 'Compromis signé',
    compromis_fell_through: 'Chute de compromis', sale_cancelled: 'Vente annulée',
    status_changed: 'Changement de statut', data_corrected: 'Correction',
    suspended: 'Suspension', reactivated: 'Réactivation', archived: 'Archivage',
    price_changed: 'Prix modifié', commission_changed: 'Commission modifiée',
};

const DIRECTIONS = [
    { key: '', label: 'Tous' },
    { key: 'positive', label: 'Sorties positives' },
    { key: 'negative', label: 'Sorties négatives' },
];

function eur(n) {
    if (n === null || n === undefined) return 'n/d';
    return new Intl.NumberFormat('fr-FR', { maximumFractionDigits: 0 }).format(n) + ' €';
}

function fdate(iso) {
    return iso ? new Date(iso).toLocaleDateString('fr-FR') : '';
}

const Delta = ({ current, previous, invert = false, pct = false }) => {
    if (previous === null || previous === undefined || current === null || current === undefined) return null;
    const diff = current - previous;
    if (diff === 0) return <span className="text-[10px] text-muted-foreground">= période préc.</span>;
    const good = invert ? diff < 0 : diff > 0;
    const label = pct && previous !== 0
        ? `${diff > 0 ? '+' : ''}${Math.round((diff / Math.abs(previous)) * 100)} %`
        : `${diff > 0 ? '+' : ''}${Math.round(diff)}`;
    return (
        <span className="inline-flex items-center gap-0.5 text-[11px] font-semibold"
              style={{ color: good ? '#16a34a' : '#dc2626' }}
              title="Évolution vs période précédente">
            {diff > 0 ? <ArrowUpRight className="w-3 h-3" /> : <ArrowDownRight className="w-3 h-3" />}
            {label}
        </span>
    );
};

const StatCard = ({ icon: Icon, label, value, tone = 'default', testId, delta = null, extra = null }) => (
    <div data-testid={testId}
         className="rounded-xl p-4 border"
         style={{ background: 'var(--card)', borderColor: 'var(--border)' }}>
        <div className="flex items-center gap-2 mb-1.5">
            <Icon className="w-4 h-4"
                  style={{ color: tone === 'pos' ? '#16a34a' : tone === 'neg' ? '#dc2626' : '#681793' }} />
            <span className="text-[11px] uppercase tracking-wider"
                  style={{ color: 'var(--muted-foreground)' }}>{label}</span>
        </div>
        <p className="text-2xl font-bold tabular-nums">{value}</p>
        {delta}
        {extra}
    </div>
);

const TargetProgress = ({ realized, target }) => {
    if (!target?.ca_target) return null;
    const pct = Math.round(((realized || 0) / target.ca_target) * 100);
    return (
        <div className="mt-2" data-testid="target-progress-bar">
            <div className="h-1.5 rounded-full overflow-hidden" style={{ background: 'var(--muted)' }}>
                <div className="h-full rounded-full transition-all"
                     style={{ width: `${Math.min(100, pct)}%`,
                              background: pct >= 100 ? '#16a34a' : pct >= 60 ? '#681793' : '#f59e0b' }} />
            </div>
            <p className="text-[10px] mt-1" style={{ color: 'var(--muted-foreground)' }}>
                <span className="font-bold" style={{ color: pct >= 100 ? '#16a34a' : 'inherit' }}>{pct} %</span>
                {' '}de l'objectif ({eur(target.ca_target)})
                {target.months_total > 1 && ` · ${target.months_covered}/${target.months_total} mois avec objectif`}
            </p>
        </div>
    );
};

export default function PilotagePage() {
    const [period, setPeriod] = useState('month');
    const [portfolio, setPortfolio] = useState(null);
    const [finance, setFinance] = useState(null);
    const [movements, setMovements] = useState(null);
    const [direction, setDirection] = useState('');
    const [page, setPage] = useState(1);
    const [loading, setLoading] = useState(true);
    const [exporting, setExporting] = useState(false);
    const [showAgenda, setShowAgenda] = useState(false);
    const [showImport, setShowImport] = useState(false);
    const [actionsRefresh, setActionsRefresh] = useState(0);

    const load = useCallback(async () => {
        setLoading(true);
        try {
            const [p, f] = await Promise.all([
                api.get(`/pilotage/portfolio/summary?period=${period}&compare=true`),
                api.get(`/pilotage/finance/summary?period=${period}&compare=true`),
            ]);
            setPortfolio(p.data);
            setFinance(f.data);
        } catch (e) {
            toast.error('Erreur de chargement du pilotage');
        } finally { setLoading(false); }
    }, [period]);

    const loadMovements = useCallback(async () => {
        try {
            const params = new URLSearchParams({ period, page: String(page), page_size: '15' });
            if (direction) params.set('direction', direction);
            const r = await api.get(`/pilotage/movements?${params}`);
            setMovements(r.data);
        } catch (e) { /* silencieux */ }
    }, [period, page, direction]);

    useEffect(() => { load(); }, [load]);
    useEffect(() => { loadMovements(); }, [loadMovements]);
    useEffect(() => { setPage(1); }, [period, direction]);

    const exportXlsx = async () => {
        setExporting(true);
        try {
            const r = await api.get(`/pilotage/export/xlsx?period=${period}`,
                                    { responseType: 'blob' });
            const url = URL.createObjectURL(r.data);
            const a = document.createElement('a');
            a.href = url;
            a.download = `homly-pilotage-${period}.xlsx`;
            a.click();
            URL.revokeObjectURL(url);
            toast.success('Export Excel téléchargé');
        } catch (e) {
            toast.error("Échec de l'export Excel");
        } finally { setExporting(false); }
    };

    const totalPages = movements ? Math.max(1, Math.ceil(movements.total / 15)) : 1;
    const realized = finance?.realized;

    return (
        <div className="space-y-6" data-testid="pilotage-page">
            {/* En-tête */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <BarChart3 className="w-6 h-6" style={{ color: '#681793' }} />
                        Pilotage commercial
                    </h1>
                    <p className="text-sm mt-0.5" style={{ color: 'var(--muted-foreground)' }}>
                        Mouvements, portefeuille et chiffre d'affaires — source unique, bornes Europe/Paris.
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-wrap">
                    <div className="flex rounded-lg border overflow-hidden"
                         style={{ borderColor: 'var(--border)' }}>
                        {PERIODS.map((p) => (
                            <button key={p.key} type="button"
                                    data-testid={`pilotage-period-${p.key}`}
                                    onClick={() => setPeriod(p.key)}
                                    className="px-3 py-1.5 text-sm font-medium transition"
                                    style={period === p.key
                                        ? { background: '#681793', color: '#fff' }
                                        : { color: 'var(--muted-foreground)' }}>
                                {p.label}
                            </button>
                        ))}
                    </div>
                    <button type="button" onClick={() => setShowAgenda(true)}
                            data-testid="pilotage-meeting-agenda-btn"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border"
                            style={{ borderColor: '#681793', color: '#681793' }}>
                        <ClipboardList className="w-4 h-4" />
                        Ordre du jour
                    </button>
                    <button type="button" onClick={() => setShowImport(true)}
                            data-testid="pilotage-import-btn"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold border"
                            style={{ borderColor: 'var(--border)' }}>
                        <FileUp className="w-4 h-4" />
                        Import historique
                    </button>
                    <button type="button" onClick={exportXlsx} disabled={exporting}
                            data-testid="pilotage-export-xlsx-btn"
                            className="flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm font-semibold text-white disabled:opacity-60"
                            style={{ background: 'linear-gradient(135deg, #681793, #9333ea)' }}>
                        {exporting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
                        Export Excel
                    </button>
                </div>
            </div>

            <PilotageAlertsBanner />

            {loading || !portfolio || !finance ? (
                <div className="flex justify-center py-16">
                    <Loader2 className="w-6 h-6 animate-spin" style={{ color: '#681793' }} />
                </div>
            ) : (
                <>
                    {/* Portefeuille */}
                    <section>
                        <div className="flex items-center gap-2 mb-2">
                            <h2 className="text-sm font-semibold uppercase tracking-wider"
                                style={{ color: 'var(--muted-foreground)' }}>Portefeuille de mandats</h2>
                            {portfolio.equation_ok ? (
                                <span data-testid="equation-badge" className="flex items-center gap-1 text-[11px] text-emerald-600">
                                    <CheckCircle2 className="w-3.5 h-3.5" /> Équation vérifiée
                                </span>
                            ) : (
                                <span data-testid="equation-badge" className="flex items-center gap-1 text-[11px] text-amber-600">
                                    <AlertTriangle className="w-3.5 h-3.5" /> Historique incomplet
                                </span>
                            )}
                        </div>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            <StatCard icon={Package} label="Stock début" value={portfolio.stock_start} testId="stat-stock-start"
                                      delta={<Delta current={portfolio.stock_start} previous={portfolio.previous?.stock_start} />} />
                            <StatCard icon={ArrowDownToLine} label="Entrées" value={`+${portfolio.entries}`} tone="pos" testId="stat-entries"
                                      delta={<Delta current={portfolio.entries} previous={portfolio.previous?.entries} />} />
                            <StatCard icon={TrendingUp} label="Ventes" value={portfolio.positive_exits} tone="pos" testId="stat-positive-exits"
                                      delta={<Delta current={portfolio.positive_exits} previous={portfolio.previous?.positive_exits} />} />
                            <StatCard icon={ArrowUpFromLine} label="Sorties négatives" value={portfolio.negative_exits} tone="neg" testId="stat-negative-exits"
                                      delta={<Delta current={portfolio.negative_exits} previous={portfolio.previous?.negative_exits} invert />} />
                            <StatCard icon={Package} label="Renouvellements" value={portfolio.renewals} testId="stat-renewals"
                                      delta={<Delta current={portfolio.renewals} previous={portfolio.previous?.renewals} />} />
                            <StatCard icon={Package} label="Stock fin" value={portfolio.stock_end} testId="stat-stock-end"
                                      delta={<Delta current={portfolio.stock_end} previous={portfolio.previous?.stock_end} />} />
                        </div>
                    </section>

                    {/* Finance */}
                    <section>
                        <h2 className="text-sm font-semibold uppercase tracking-wider mb-2"
                            style={{ color: 'var(--muted-foreground)' }}>Chaîne financière (HT)</h2>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                            <StatCard icon={Euro} label="Potentiel" value={eur(finance.potential?.total_ht)} testId="fin-potential" />
                            <StatCard icon={Euro} label="Pipeline pondéré" value={eur(finance.weighted_pipeline?.total_ht)} testId="fin-weighted" />
                            <StatCard icon={Euro} label="Sécurisé" value={eur(finance.secured?.total_ht)} testId="fin-secured"
                                      delta={<Delta current={finance.secured?.total_ht} previous={finance.previous?.secured} pct />} />
                            <StatCard icon={Euro} label="CA réalisé" value={eur(realized?.total_ht)} tone="pos" testId="fin-realized"
                                      delta={<Delta current={realized?.total_ht} previous={finance.previous?.realized} pct />}
                                      extra={<TargetProgress realized={realized?.total_ht} target={finance.target} />} />
                            <StatCard icon={Euro} label="Facturé" value={finance.invoiced?.available ? eur(finance.invoiced.total_ht) : 'n/d'} testId="fin-invoiced"
                                      delta={finance.invoiced?.available ? <Delta current={finance.invoiced?.total_ht} previous={finance.previous?.invoiced} pct /> : null} />
                            <StatCard icon={Euro} label="Encaissé" value={finance.collected?.available ? eur(finance.collected.total_ht) : 'n/d'} testId="fin-collected"
                                      delta={finance.collected?.available ? <Delta current={finance.collected?.total_ht} previous={finance.previous?.collected} pct /> : null} />
                        </div>
                        <p className="text-[11px] mt-1.5" style={{ color: 'var(--muted-foreground)' }}>
                            CA réalisé — source : {realized?.source === 'commissions' ? 'commissions enregistrées' : 'dérivé des mandats vendus (estimation)'}
                            {realized?.data_quality === 'estimated' && ' · qualité : estimée'}
                            {' · '}Facturé/Encaissé : saisis depuis la page Commissions.
                        </p>
                    </section>

                    {/* CA par agent */}
                    <section>
                        <h2 className="text-sm font-semibold uppercase tracking-wider mb-2"
                            style={{ color: 'var(--muted-foreground)' }}>Production par agent</h2>
                        <div className="rounded-xl border overflow-hidden"
                             style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                             data-testid="ca-by-agent-table">
                            {(finance.by_agent || []).length === 0 ? (
                                <p className="p-4 text-sm" style={{ color: 'var(--muted-foreground)' }}>
                                    Aucune production enregistrée sur la période.
                                </p>
                            ) : (
                                <table className="w-full text-sm">
                                    <thead>
                                        <tr className="text-left text-[11px] uppercase tracking-wider"
                                            style={{ color: 'var(--muted-foreground)' }}>
                                            <th className="px-4 py-2.5">Agent</th>
                                            <th className="px-4 py-2.5 text-right">Production HT</th>
                                            <th className="px-4 py-2.5 text-right">Affaires</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {finance.by_agent.map((a) => (
                                            <tr key={a.agent_id} className="border-t"
                                                style={{ borderColor: 'var(--border)' }}
                                                data-testid={`ca-agent-row-${a.agent_id}`}>
                                                <td className="px-4 py-2.5 font-medium">{a.agent_name || a.agent_id}</td>
                                                <td className="px-4 py-2.5 text-right tabular-nums font-semibold"
                                                    style={{ color: '#681793' }}>{eur(a.production_ht)}</td>
                                                <td className="px-4 py-2.5 text-right tabular-nums">{a.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </section>

                    <TargetsHistoryChart />

                    <PilotageActionsPanel refreshKey={actionsRefresh} />

                    {/* Mouvements */}
                    <section>
                        <div className="flex items-center justify-between gap-2 mb-2 flex-wrap">
                            <h2 className="text-sm font-semibold uppercase tracking-wider"
                                style={{ color: 'var(--muted-foreground)' }}>
                                Mouvements de mandats {movements ? `(${movements.total})` : ''}
                            </h2>
                            <div className="flex rounded-lg border overflow-hidden"
                                 style={{ borderColor: 'var(--border)' }}>
                                {DIRECTIONS.map((d) => (
                                    <button key={d.key} type="button"
                                            data-testid={`movements-filter-${d.key || 'all'}`}
                                            onClick={() => setDirection(d.key)}
                                            className="px-2.5 py-1 text-xs font-medium"
                                            style={direction === d.key
                                                ? { background: '#681793', color: '#fff' }
                                                : { color: 'var(--muted-foreground)' }}>
                                        {d.label}
                                    </button>
                                ))}
                            </div>
                        </div>
                        <div className="rounded-xl border overflow-x-auto"
                             style={{ borderColor: 'var(--border)', background: 'var(--card)' }}
                             data-testid="movements-table">
                            <table className="w-full text-sm min-w-[640px]">
                                <thead>
                                    <tr className="text-left text-[11px] uppercase tracking-wider"
                                        style={{ color: 'var(--muted-foreground)' }}>
                                        <th className="px-4 py-2.5">Date</th>
                                        <th className="px-4 py-2.5">Événement</th>
                                        <th className="px-4 py-2.5">Agent</th>
                                        <th className="px-4 py-2.5">Statut</th>
                                        <th className="px-4 py-2.5">Motif</th>
                                        <th className="px-4 py-2.5">Source</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {(movements?.movements || []).map((m) => (
                                        <tr key={m.id} className="border-t"
                                            style={{ borderColor: 'var(--border)' }}>
                                            <td className="px-4 py-2 whitespace-nowrap">{fdate(m.effective_at)}</td>
                                            <td className="px-4 py-2">
                                                <span className="px-2 py-0.5 rounded-full text-[11px] font-medium"
                                                      style={m.event_type === 'sold'
                                                          ? { background: 'rgba(22,163,74,0.12)', color: '#16a34a' }
                                                          : ['denounced', 'terminated', 'expired', 'withdrawn'].includes(m.event_type)
                                                              ? { background: 'rgba(220,38,38,0.12)', color: '#dc2626' }
                                                              : { background: 'rgba(104,23,147,0.10)', color: '#681793' }}>
                                                    {EVENT_LABELS[m.event_type] || m.event_type}
                                                </span>
                                                {m.is_backfilled && (
                                                    <span className="ml-1.5 text-[9px] uppercase"
                                                          style={{ color: 'var(--muted-foreground)' }}
                                                          title={`Événement reconstruit (confiance ${m.backfill_confidence})`}>
                                                        reconstruit
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-2">{m.agent_name || '—'}</td>
                                            <td className="px-4 py-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                                {m.old_status_canonical ? `${m.old_status_canonical} → ` : ''}{m.new_status_canonical}
                                            </td>
                                            <td className="px-4 py-2 text-xs">{m.reason_label || m.reason_code || '—'}</td>
                                            <td className="px-4 py-2 text-xs" style={{ color: 'var(--muted-foreground)' }}>{m.source}</td>
                                        </tr>
                                    ))}
                                    {movements && movements.movements.length === 0 && (
                                        <tr><td colSpan={6} className="px-4 py-6 text-center text-sm"
                                                style={{ color: 'var(--muted-foreground)' }}>
                                            Aucun mouvement sur la période.
                                        </td></tr>
                                    )}
                                </tbody>
                            </table>
                        </div>
                        {totalPages > 1 && (
                            <div className="flex items-center justify-end gap-2 mt-2">
                                <button type="button" disabled={page <= 1}
                                        data-testid="movements-prev-page"
                                        onClick={() => setPage((p) => p - 1)}
                                        className="p-1.5 rounded-lg border disabled:opacity-40"
                                        style={{ borderColor: 'var(--border)' }}>
                                    <ChevronLeft className="w-4 h-4" />
                                </button>
                                <span className="text-xs" style={{ color: 'var(--muted-foreground)' }}>
                                    {page} / {totalPages}
                                </span>
                                <button type="button" disabled={page >= totalPages}
                                        data-testid="movements-next-page"
                                        onClick={() => setPage((p) => p + 1)}
                                        className="p-1.5 rounded-lg border disabled:opacity-40"
                                        style={{ borderColor: 'var(--border)' }}>
                                    <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        )}
                    </section>
                </>
            )}

            <MeetingAgendaDialog open={showAgenda} onClose={() => setShowAgenda(false)} period={period}
                                 onActionCreated={() => setActionsRefresh((k) => k + 1)} />
            <ImportHistoryDialog open={showImport} onClose={() => setShowImport(false)}
                                 onImported={() => { load(); loadMovements(); }} />
        </div>
    );
}
